import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  ChartTooltip,
  ChartTooltipContent,
  ChartContainer,
} from "@/components/ui/chart";
import { PolarAngleAxis, PolarGrid, Radar, RadarChart } from "recharts";
import { useRouter } from "next/router";
import Loader from "@/components/Loader";
import { BadgeAlert, BadgeCheck, ExternalLinkIcon } from "lucide-react";
import Link from "next/link";
import {
  Pagination,
  formatDate,
  minFixedDecimal,
  DP22,
  formatTrade,
} from "@/helpers/helperFunctions";

import { usePostMarketPlaceFilterAuthors } from "@/api/marketPlaceApi";

const filtersAuthors = {
  sort_order: "top_traders",
  total_strategies: "",
  copiers: "",
  trading_profile: "",
  win_p: "",
};

export default function AuthorsMarketplace() {
  const router = useRouter();
  let [pageAuthors, setPageAuthors] = useState(1);
  let [dataSize, setDatasize] = useState(6);
  let [totalDataLengthAuthors, setTotalDataLengthAuthors] = useState(0);
  let [dataAuthors, setDataAuthors] = useState([]);
  const formData = Object.entries(filtersAuthors).reduce((fd, [key, value]) => {
    if (value !== undefined && value !== null) fd.append(key, value);
    return fd;
  }, new FormData());

  const {
    data,
    isLoading: loadingAuthors,
    isSuccess,
  } = usePostMarketPlaceFilterAuthors(
    formData,
    String(pageAuthors),
    String(dataSize)
  );

  useEffect(() => {
    if (isSuccess && data) {
      setDataAuthors(data.data.items);
      setTotalDataLengthAuthors(data.data.total);
    }
  }, [isSuccess, data, loadingAuthors]);

  const getNameInitials = (name: any) => {
    const initials = name
      .split(" ")
      .slice(0, 2)
      .map((word: any) => word.charAt(0))
      .join("");
    return initials.toUpperCase();
  };

  return (
    <Card className="w-full h-full relative ">
      {/* <div className="max-h-[45vh] lg:max-h-[70vh] overflow-scroll"> */}
      <CardHeader className="flex !p-3 px-6 justify-between flex-row items-center w-full">
        <CardTitle className="text-xl flex gap-2 items-center text-nowrap">
          Top Traders
        </CardTitle>

        <Link
          href="/marketplace?showtraders=true"
          className="flex text-xs hover:underline text-[var(--marketplace-blue-new)] font-medium"
        >
          Explore More <ExternalLinkIcon className="h-[15px]" />
        </Link>
      </CardHeader>
      {loadingAuthors || dataAuthors.length == 0 ? (
        !loadingAuthors ? (
          <div className="w-full flex items-center justify-center h-[200px]">
            No data found
          </div>
        ) : (
          <div className="w-full flex items-center justify-center h-[200px]">
            <Loader />
          </div>
        )
      ) : (
        <CardContent className="p-3 pt-0 h-[500px] overflow-auto">
          <div className="flex flex-wrap w-full justify-between">
            {dataAuthors.map((each: any, i: number) => {
              let firstName = each?.user?.first_name?.split(" ")[0];
              let lastName = each?.user?.last_name?.split(" ")[0];
              let fullName = firstName + " " + lastName;

              let badgeValue = JSON.parse(
                each?.meta_data?.user_meta?.badge
              )?.last_badge;

              let allCategories = each?.meta_data?.trading_profile_categories;
              let profileData = each?.meta_data?.trading_profile_graph;
              let graphData: any = [];
              profileData &&
                Object.keys(profileData).forEach((key) => {
                  graphData.push({
                    name: key,
                    value: profileData?.[key],
                  });
                });
              let missingData = allCategories
                .filter(
                  (item: any) =>
                    !graphData.some((data: any) => data.name === item)
                )
                .map((item: any) => ({ name: item, value: 0 }));
              let newData = [...graphData, ...missingData];
              return (
                <Card
                  key={each.user.email}
                  className="p-2 w-full  2xl:w-[49.3%] mb-2 pb-3 pt-2 "
                >
                  <div
                    className="flex items-center w-fit cursor-pointer text-sm border-none"
                    onClick={() =>
                      router.push(
                        `/profile/${each.user.unique_key}?locale=${
                          router.query.locale || "en"
                        }`
                      )
                    }
                  >
                    <div
                      className={cn(
                        "flex h-full w-full items-center justify-center rounded-full bg-muted w-[28px] h-[28px] mr-2 text-xs "
                      )}
                    >
                      {getNameInitials(fullName)}
                    </div>
                    {fullName}

                    {badgeValue == "rock" ? (
                      <BadgeAlert className="text-[#9B0505] w-[16px] h-[16px] ml-2" />
                    ) : badgeValue == "bronze" ? (
                      <BadgeCheck className="text-[#cd7f32] w-[16px] h-[16px] ml-2" />
                    ) : badgeValue == "silver" ? (
                      <BadgeCheck className="text-[#19438A] w-[16px] h-[16px] ml-2" />
                    ) : badgeValue == "gold" ? (
                      <BadgeCheck className="text-[#FFD700] w-[16px] h-[16px] ml-2" />
                    ) : (
                      ""
                    )}
                    {/* <ExternalLinkIcon className="text-muted-foreground ml-2 w-[16px] h-[16px]" /> */}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-[4px]">
                    Member since{" "}
                    {formatDate(each?.user?.created_at, "DD-MMM-YY")}
                  </div>
                  <div className="sm:flex sm:justify-between mt-2">
                    <div
                      className="w-full sm:w-[49%] 2xl:w-[45%] flex items-center justify-center "
                      id="section1-chart"
                    >
                      <ChartContainer
                        config={{
                          value: {
                            color: "rgba(72,109,246,1)",
                          },
                        }}
                        className="w-[100%] h-full"
                      >
                        <RadarChart
                          data={newData}
                          margin={{
                            top: 12,
                            right: 12,
                            left: 12,
                            bottom: 12,
                          }}
                        >
                          <ChartTooltip
                            cursor={false}
                            content={
                              <ChartTooltipContent
                                formatter={(value) =>
                                  minFixedDecimal(value as number, DP22) + "%"
                                }
                              />
                            }
                          />
                          <PolarAngleAxis dataKey="name" />
                          <PolarGrid />
                          <Radar
                            dataKey="value"
                            fill="var(--color-value)"
                            dot
                            fillOpacity={0.6}
                          />
                        </RadarChart>
                      </ChartContainer>
                    </div>
                    <div className="w-full sm:w-[49%] 2xl:w-[55%] text-nowrap flex flex-col justify-between">
                      <div className="flex items-end justify-between ">
                        <div className="w-[55%]">
                          <div className="text-3xl 2xl:text-2xl font-bold">
                            {each?.meta_data?.win_p >= 75 ? (
                              <div className="text-[--marketplace-green] items-end">
                                {each?.meta_data?.win_p?.toFixed(0)}
                                <span className="text-base">%</span>
                              </div>
                            ) : each?.meta_data?.win_p >= 50 ? (
                              <div className="text-[--marketplace-blue] items-end">
                                {each?.meta_data?.win_p?.toFixed(0)}
                                <span className="text-base">%</span>
                              </div>
                            ) : each?.meta_data?.win_p >= 0 ? (
                              <div className="text-[--marketplace-red] items-end">
                                {each?.meta_data?.win_p?.toFixed(0)}
                                <span className="text-base">%</span>
                              </div>
                            ) : (
                              "NA"
                            )}
                          </div>
                          <div className="text-[10px] text-[var(--marketplace-grey)] font-semibold">
                            Average Win
                          </div>
                        </div>

                        <div className="w-[45%] ">
                          <div className="text-xl font-medium">
                            {formatTrade(each?.meta_data?.copiers_count)}
                          </div>
                          <div className="text-[10px] text-[var(--marketplace-grey)] font-semibold">
                            Copiers Count
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start mt-2 pb-2">
                        <div className="w-[55%]">
                          <div className="text-[10px] text-[var(--marketplace-grey)] font-semibold">
                            Total Strategies
                          </div>
                          <div className="text-xl font-medium">
                            {formatTrade(each?.meta_data?.total_strategies)}
                          </div>
                        </div>
                        <div className="w-[45%]">
                          <div className="text-[10px] text-[var(--marketplace-grey)] font-semibold">
                            Total Trades
                          </div>
                          <div className="text-xl font-medium">
                            {formatTrade(each?.meta_data?.total_trades)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          <div className="mt-3 mb-10 sm:mb-0">
            <Pagination
              totalCount={totalDataLengthAuthors}
              currentPage={pageAuthors}
              changePage={setPageAuthors}
              limit={dataSize}
            />
          </div>
        </CardContent>
      )}
      {/* </div> */}
    </Card>
  );
}
